import { useLazyQuery, useMutation, useQuery } from "@apollo/client";
import Add from "@mui/icons-material/Add";
import DeleteOutline from "@mui/icons-material/DeleteOutline";
import EditOutlined from "@mui/icons-material/EditOutlined";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { Dialog } from "components/Dialog";
import { DateField } from "components/Forms/DateField";
import { FormTextField } from "components/Forms/FormTextField";
import { IssueListButton } from "components/Forms/IssueListButton";
import {
  GetWasteShipmentQuery,
  Issue,
  UnitType,
  WasteShipmentStatus,
} from "generated-graphql/graphql";
import { useBreadcrumb } from "hooks/useBreadcrumbs";
import { useTenant } from "hooks/useTenant";
import { useValidatingForm } from "hooks/useValidatingForm";
import { DateTime } from "luxon";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Controller, FormProvider } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { toUtcMidnightIsoFromDateLike } from "util/dates";
import { hasCriticalIssues } from "util/forms";

import { formatQuantity, NO_VALUE_PLACEHOLDER, UNIT_LABELS } from "../Containers/containerDisplay";
import {
  CANCEL_WASTE_SHIPMENT_MUTATION,
  GET_WASTE_SHIPMENT_QUERY,
  SHIPPABLE_CONTAINERS_QUERY,
  TENANT_WASTE_VENDOR_OPTIONS_QUERY,
  UPDATE_SHIPMENT_LINES_MUTATION,
  UPDATE_WASTE_SHIPMENT_MUTATION,
  VALIDATE_UPDATE_SHIPMENT_LINES_INPUT,
  VALIDATE_UPDATE_WASTE_SHIPMENT_INPUT,
} from "./api";
import { formatShipmentDate, formatTotalsByUnit } from "./shipmentDisplay";
import { WasteShipmentStatusChip } from "./WasteShipmentStatusChip";

type Shipment = NonNullable<GetWasteShipmentQuery["wasteShipment"]>;
type ShipmentLine = Shipment["lines"][number];
type VendorOption = { id: string; name: string; usedByTenant: boolean };

const REFETCH = ["GetWasteShipment", "FacilityWasteShipments", "TenantWasteShipments"];

function SectionCard({
  title,
  action,
  children,
}: {
  title: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <Paper elevation={0} sx={{ p: 3 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography variant="h6">{title}</Typography>
        {action}
      </Stack>
      {children}
    </Paper>
  );
}

function FieldValue({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
      <Typography variant="caption" color="text.secondary" display="block">
        {label}
      </Typography>
      <Typography variant="body2">{children ?? NO_VALUE_PLACEHOLDER}</Typography>
    </Grid>
  );
}

const isEditable = (status: WasteShipmentStatus) =>
  status === WasteShipmentStatus.Draft || status === WasteShipmentStatus.Requested;

export function WasteShipmentDetail() {
  const { tenantId } = useTenant();
  const { shipmentId } = useParams<{ shipmentId: string }>();
  const navigate = useNavigate();
  const alerts = useAlerts();

  const { data, loading } = useQuery(GET_WASTE_SHIPMENT_QUERY, {
    variables: { id: shipmentId ?? "" },
    skip: !shipmentId,
  });
  const shipment = data?.wasteShipment ?? null;

  const [addOpen, setAddOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [editLine, setEditLine] = useState<ShipmentLine | null>(null);

  useBreadcrumb([
    { label: "Waste", to: tenantId ? `/o/${tenantId}/waste` : undefined },
    { label: "Shipments", to: tenantId ? `/o/${tenantId}/waste/shipments` : undefined },
    { label: shipment?.manifestRequestNumber ?? "Shipment" },
  ]);

  const [updateLines] = useMutation(UPDATE_SHIPMENT_LINES_MUTATION, {
    refetchQueries: REFETCH,
  });
  const [cancelShipment, { loading: canceling }] = useMutation(CANCEL_WASTE_SHIPMENT_MUTATION, {
    refetchQueries: REFETCH,
  });

  if (loading && !shipment) {
    return (
      <Stack spacing={3} sx={{ py: 2 }}>
        <Stack direction="row" alignItems="center" spacing={2} sx={{ px: 3 }}>
          <Skeleton variant="text" width={130} height={36} />
          <Skeleton variant="rounded" width={72} height={24} />
        </Stack>
        <Paper elevation={0} sx={{ p: 3 }}>
          <Skeleton variant="text" width={90} height={28} sx={{ mb: 2 }} />
          <Grid container spacing={2}>
            {Array.from({ length: 9 }).map((_, i) => (
              <Grid key={i} size={{ xs: 12, sm: 6, md: 4 }}>
                <Skeleton variant="text" width={90} />
                <Skeleton variant="text" width={140} />
              </Grid>
            ))}
          </Grid>
        </Paper>
        <Grid container spacing={2}>
          {[0, 1].map((i) => (
            <Grid key={i} size={{ xs: 12, md: 6 }}>
              <Paper elevation={0} sx={{ p: 3 }}>
                <Skeleton variant="text" width={200} height={28} sx={{ mb: 2 }} />
                <Skeleton variant="rectangular" height={140} />
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Stack>
    );
  }

  if (!shipment) {
    return (
      <Box sx={{ p: 6, textAlign: "center" }}>
        <Typography variant="h6">Shipment not found</Typography>
      </Box>
    );
  }

  const editable = isEditable(shipment.status);

  const removeLine = async (line: ShipmentLine) => {
    try {
      await updateLines({
        variables: { input: { shipmentId: shipment.id, removeLineIds: [line.id] } },
      });
      alerts.success(`Removed ${line.container.containerLabel} from the shipment.`);
    } catch (e) {
      alerts.error(e instanceof Error ? e.message : "Failed to remove the container.");
    }
  };

  const handleCancel = async () => {
    try {
      await cancelShipment({ variables: { id: shipment.id } });
      alerts.success(`Shipment ${shipment.manifestRequestNumber} canceled.`);
      setCancelOpen(false);
    } catch (e) {
      alerts.error(e instanceof Error ? e.message : "Failed to cancel the shipment.");
    }
  };

  return (
    <Stack spacing={3} sx={{ py: 2 }}>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ px: 3 }}>
        <Typography variant="h5">{shipment.manifestRequestNumber}</Typography>
        <WasteShipmentStatusChip status={shipment.status} />
        {shipment.backfilledFromManifest && (
          <Chip label="Backfilled from manifest" size="small" variant="outlined" />
        )}
        <Box sx={{ flexGrow: 1 }} />
        {editable && (
          <Button variant="outlined" size="small" onClick={() => setCancelOpen(true)}>
            Cancel shipment
          </Button>
        )}
      </Stack>

      {editable ? (
        <ShipmentDetailsForm shipment={shipment} />
      ) : (
        <SectionCard title="Details">
          <Grid container spacing={2}>
            <FieldValue label="Facility">{shipment.facility?.name}</FieldValue>
            <FieldValue label="Vendor">{shipment.vendor?.name}</FieldValue>
            <FieldValue label="Transporter">{shipment.transporterName}</FieldValue>
            <FieldValue label="Destination (TSDF)">{shipment.destinationName}</FieldValue>
            <FieldValue label="Destination EPA ID">{shipment.destinationEpaId}</FieldValue>
            <FieldValue label="Planned ship date">
              {formatShipmentDate(shipment.plannedShipDate)}
            </FieldValue>
            <FieldValue label="Scheduled pickup">
              {formatShipmentDate(shipment.scheduledPickupDate)}
            </FieldValue>
            <FieldValue label="Shipped">{formatShipmentDate(shipment.shippedDate)}</FieldValue>
            <FieldValue label="Total">
              {formatTotalsByUnit(shipment.totalQuantityByUnit)}
            </FieldValue>
          </Grid>
        </SectionCard>
      )}

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 6 }}>
          <SectionCard title="Manifest request (by waste stream)">
            <Alert severity="info" sx={{ mb: 2 }}>
              This is the vendor view — not an official manifest.
            </Alert>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Waste stream</TableCell>
                  <TableCell align="right">Containers</TableCell>
                  <TableCell align="right">Quantity</TableCell>
                  <TableCell>Codes</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {shipment.manifestRequestLines.map((line, i) => (
                  <TableRow key={`${line.wasteStream?.id ?? "none"}-${line.unit}-${i}`}>
                    <TableCell>{line.wasteStream?.name ?? "Uncharacterized"}</TableCell>
                    <TableCell align="right">{line.containerCount}</TableCell>
                    <TableCell align="right">{formatQuantity(line.quantity, line.unit)}</TableCell>
                    <TableCell>
                      {[...line.federalWasteCodes, ...line.stateWasteCodes].join(", ") ||
                        NO_VALUE_PLACEHOLDER}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </SectionCard>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <SectionCard
            title="Containers"
            action={
              editable ? (
                <Button size="small" startIcon={<Add />} onClick={() => setAddOpen(true)}>
                  Add containers
                </Button>
              ) : undefined
            }
          >
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Container</TableCell>
                  <TableCell>Waste stream</TableCell>
                  <TableCell align="right">Quantity</TableCell>
                  {editable && <TableCell align="right" />}
                </TableRow>
              </TableHead>
              <TableBody>
                {shipment.lines.map((line) => (
                  <TableRow key={line.id}>
                    <TableCell>{line.container.containerLabel}</TableCell>
                    <TableCell>{line.wasteStream?.name ?? "Uncharacterized"}</TableCell>
                    <TableCell align="right">{formatQuantity(line.quantity, line.unit)}</TableCell>
                    {editable && (
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          aria-label={`Edit quantity for ${line.container.containerLabel}`}
                          onClick={() => setEditLine(line)}
                        >
                          <EditOutlined fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          aria-label={`Remove ${line.container.containerLabel}`}
                          onClick={() => removeLine(line)}
                        >
                          <DeleteOutline fontSize="small" />
                        </IconButton>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </SectionCard>
        </Grid>
      </Grid>

      {addOpen && <AddContainersDialog shipment={shipment} onClose={() => setAddOpen(false)} />}
      {editLine && (
        <EditLineQuantityDialog
          shipmentId={shipment.id}
          line={editLine}
          onClose={() => setEditLine(null)}
        />
      )}
      {cancelOpen && (
        <Dialog
          open
          onClose={() => setCancelOpen(false)}
          maxWidth="xs"
          title="Cancel this shipment?"
          primaryActionButtonProps={{
            saveText: "Cancel shipment",
            loading: canceling,
            onClick: handleCancel,
          }}
        >
          <Typography variant="body2">
            {shipment.manifestRequestNumber} will be marked canceled. No container amounts change —
            removals only happen when a shipment ships.
          </Typography>
        </Dialog>
      )}
    </Stack>
  );
}
type EditShipmentFormValues = {
  vendorId: string | null;
  transporterName: string;
  destinationName: string;
  destinationEpaId: string;
  plannedShipDate: string | null;
};

function toUpdateShipmentInput(
  shipmentId: string,
  values: EditShipmentFormValues,
  plannedShipDateDirty: boolean
) {
  return {
    id: shipmentId,
    vendorId: values.vendorId,
    transporterName: values.transporterName.trim() || null,
    destinationName: values.destinationName.trim() || null,
    destinationEpaId: values.destinationEpaId.trim() || null,
    // Only re-normalize the date when the user actually touched it, so an
    // untouched value can't shift a day through the local timezone.
    ...(plannedShipDateDirty
      ? {
          plannedShipDate: values.plannedShipDate
            ? toUtcMidnightIsoFromDateLike(DateTime.fromISO(values.plannedShipDate))
            : null,
        }
      : {}),
  };
}

/**
 * Async header-edit validation; the seam future field checks land in. The
 * validation payload always includes the date (nothing persists from it, so
 * the dirty-gated timezone guard only matters on the real mutation input).
 */
function useShipmentUpdateValidation(shipmentId: string) {
  const [query] = useLazyQuery(VALIDATE_UPDATE_WASTE_SHIPMENT_INPUT, {
    fetchPolicy: "no-cache",
  });
  return useCallback(
    async (values: EditShipmentFormValues): Promise<Issue[]> => {
      const { data } = await query({
        variables: { input: toUpdateShipmentInput(shipmentId, values, true) },
      });
      return data?.wasteShipmentUpdateInputValidator ?? [];
    },
    [query, shipmentId]
  );
}

/**
 * The shipment's header as a live inline form — the single place vendor,
 * transporter, TSDF, and dates are collected. Save/Discard appear only when
 * dirty; background refetches keep in-progress edits (keepDirtyValuesOnReset).
 * 3C note: once requests generate, a header edit after requestGeneratedAt
 * should surface a stale-request warning here.
 */
function ShipmentDetailsForm({ shipment }: { shipment: Shipment }) {
  const { tenantId } = useTenant();
  const alerts = useAlerts();
  const { data: vendorData, loading: vendorsLoading } = useQuery(
    TENANT_WASTE_VENDOR_OPTIONS_QUERY,
    { variables: { tenantId: tenantId ?? "" }, skip: !tenantId }
  );
  const vendorOptions: VendorOption[] = vendorData?.tenant?.wasteVendorOptions ?? [];

  const defaultValues = useMemo<EditShipmentFormValues>(
    () => ({
      vendorId: shipment.vendor?.id ?? null,
      transporterName: shipment.transporterName ?? "",
      destinationName: shipment.destinationName ?? "",
      destinationEpaId: shipment.destinationEpaId ?? "",
      plannedShipDate: shipment.plannedShipDate ?? null,
    }),
    [shipment]
  );

  const form = useValidatingForm<EditShipmentFormValues>(
    defaultValues,
    [],
    useShipmentUpdateValidation(shipment.id),
    [],
    { keepDirtyValuesOnReset: true }
  );
  const {
    control,
    getValues,
    reset,
    formState: { isDirty, dirtyFields },
    issues,
  } = form;

  const [update, { loading }] = useMutation(UPDATE_WASTE_SHIPMENT_MUTATION, {
    refetchQueries: REFETCH,
  });

  const handleSave = async () => {
    if (hasCriticalIssues(issues)) {
      return;
    }
    try {
      await update({
        variables: {
          input: toUpdateShipmentInput(
            shipment.id,
            getValues(),
            Boolean(dirtyFields.plannedShipDate)
          ),
        },
      });
      alerts.success("Shipment updated.");
    } catch (e) {
      alerts.error(e instanceof Error ? e.message : "Failed to update the shipment.");
    }
  };

  return (
    <SectionCard
      title="Details"
      action={
        // Always rendered (disabled until dirty) so showing edits never
        // shifts the form's layout.
        <Stack direction="row" alignItems="center" spacing={1}>
          <IssueListButton issues={issues} />
          <Button
            size="small"
            variant="outlined"
            disabled={!isDirty}
            onClick={() => reset(defaultValues)}
          >
            Discard
          </Button>
          <Button
            size="small"
            variant="contained"
            disabled={!isDirty || hasCriticalIssues(issues) || loading}
            onClick={handleSave}
          >
            Save
          </Button>
        </Stack>
      }
    >
      <FormProvider {...form}>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <FieldValue label="Facility">{shipment.facility?.name}</FieldValue>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <Controller
              control={control}
              name="vendorId"
              render={({ field }) => (
                <Autocomplete
                  options={vendorOptions}
                  getOptionLabel={(o: VendorOption) => o.name}
                  groupBy={(o: VendorOption) => (o.usedByTenant ? "Your vendors" : "All vendors")}
                  isOptionEqualToValue={(o, v) => o.id === v.id}
                  value={vendorOptions.find((o) => o.id === field.value) ?? null}
                  onChange={(_e, value) => field.onChange(value?.id ?? null)}
                  loading={vendorsLoading}
                  fullWidth
                  renderInput={(params) => <TextField {...params} label="Vendor" size="small" />}
                  noOptionsText="No vendors found in this organization's waste data"
                />
              )}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <FormTextField
              control={control}
              name="transporterName"
              label="Transporter"
              fullWidth
              textFieldProps={{ size: "small" }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <FormTextField
              control={control}
              name="destinationName"
              label="Destination (TSDF)"
              fullWidth
              textFieldProps={{ size: "small" }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <FormTextField
              control={control}
              name="destinationEpaId"
              label="Destination EPA ID"
              fullWidth
              textFieldProps={{ size: "small" }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <DateField
              control={control}
              name="plannedShipDate"
              label="Planned ship date"
              datePickerProps={{ slotProps: { textField: { size: "small", fullWidth: true } } }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <FieldValue label="Scheduled pickup">
              {formatShipmentDate(shipment.scheduledPickupDate)}
            </FieldValue>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <FieldValue label="Shipped">{formatShipmentDate(shipment.shippedDate)}</FieldValue>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <FieldValue label="Total">
              {formatTotalsByUnit(shipment.totalQuantityByUnit)}
            </FieldValue>
          </Grid>
        </Grid>
      </FormProvider>
    </SectionCard>
  );
}

function AddContainersDialog({ shipment, onClose }: { shipment: Shipment; onClose: () => void }) {
  const alerts = useAlerts();
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const { data, loading } = useQuery(SHIPPABLE_CONTAINERS_QUERY, {
    variables: { facilityId: shipment.facilityId },
    fetchPolicy: "cache-and-network",
  });
  const onShipment = useMemo(
    () => new Set(shipment.lines.map((l) => l.container.id)),
    [shipment.lines]
  );
  const candidates = useMemo(
    () => (data?.facility?.shippableContainers?.items ?? []).filter((c) => !onShipment.has(c.id)),
    [data?.facility?.shippableContainers?.items, onShipment]
  );

  // Live warn-level validation as the selection changes — e.g. a container
  // already on another open shipment warns here just like in the builder.
  const [validate, { data: validationData }] = useLazyQuery(VALIDATE_UPDATE_SHIPMENT_LINES_INPUT, {
    fetchPolicy: "no-cache",
  });
  useEffect(() => {
    if (selectedIds.size > 0) {
      validate({
        variables: { input: { shipmentId: shipment.id, addContainerIds: [...selectedIds] } },
      });
    }
  }, [selectedIds, shipment.id, validate]);
  const issues: Issue[] =
    selectedIds.size > 0 ? (validationData?.updateShipmentLinesInputValidator ?? []) : [];

  const [updateLines, { loading: saving }] = useMutation(UPDATE_SHIPMENT_LINES_MUTATION, {
    refetchQueries: REFETCH,
  });

  const toggle = (id: string) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });

  const handleAdd = async () => {
    try {
      await updateLines({
        variables: { input: { shipmentId: shipment.id, addContainerIds: [...selectedIds] } },
      });
      alerts.success(
        `Added ${selectedIds.size} container${selectedIds.size === 1 ? "" : "s"} to the shipment.`
      );
      onClose();
    } catch (e) {
      alerts.error(e instanceof Error ? e.message : "Failed to add containers.");
    }
  };

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth="sm"
      title="Add containers"
      additionalPrimaryActionElements={[<IssueListButton key="issues" issues={issues} />]}
      primaryActionButtonProps={{
        saveText: "Add",
        loading: saving,
        disabled: selectedIds.size === 0 || hasCriticalIssues(issues),
        onClick: handleAdd,
      }}
    >
      {loading && candidates.length === 0 && <Skeleton height={120} />}
      {!loading && candidates.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          Every shippable container at this facility is already on the shipment.
        </Typography>
      )}
      <Stack sx={{ maxHeight: 360, overflowY: "auto" }}>
        {candidates.map((container) => (
          <Stack key={container.id} direction="row" alignItems="center" spacing={1.5}>
            <Checkbox
              size="small"
              checked={selectedIds.has(container.id)}
              onChange={() => toggle(container.id)}
              inputProps={{ "aria-label": `Select ${container.containerLabel}` }}
            />
            <Typography variant="subtitle2" sx={{ minWidth: 90 }}>
              {container.containerLabel}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ flexGrow: 1 }}>
              {container.wasteStream?.name ?? "Uncharacterized"}
            </Typography>
            <Typography variant="body2">
              {container.amount != null && container.amountUnit
                ? formatQuantity(container.amount, container.amountUnit)
                : NO_VALUE_PLACEHOLDER}
            </Typography>
          </Stack>
        ))}
      </Stack>
      {issues.length > 0 && (
        <Stack spacing={1} sx={{ mt: 2 }}>
          {issues.map((issue, i) => (
            <Alert
              key={`${issue.key}-${i}`}
              severity={issue.level === "CRITICAL" ? "error" : "warning"}
            >
              {issue.message}
            </Alert>
          ))}
        </Stack>
      )}
    </Dialog>
  );
}

type EditLineQuantityFormValues = {
  quantity: string;
  unit: UnitType;
};

function EditLineQuantityDialog({
  shipmentId,
  line,
  onClose,
}: {
  shipmentId: string;
  line: ShipmentLine;
  onClose: () => void;
}) {
  const alerts = useAlerts();

  const defaultValues = useMemo<EditLineQuantityFormValues>(
    () => ({ quantity: String(line.quantity), unit: line.unit }),
    [line]
  );

  const [validateQuery] = useLazyQuery(VALIDATE_UPDATE_SHIPMENT_LINES_INPUT, {
    fetchPolicy: "no-cache",
  });
  const validateInput = useCallback(
    async (values: EditLineQuantityFormValues): Promise<Issue[]> => {
      const { data } = await validateQuery({
        variables: {
          input: {
            shipmentId,
            setQuantities: [
              { lineId: line.id, quantity: Number(values.quantity), unit: values.unit },
            ],
          },
        },
      });
      return data?.updateShipmentLinesInputValidator ?? [];
    },
    [validateQuery, shipmentId, line.id]
  );

  const form = useValidatingForm<EditLineQuantityFormValues>(defaultValues, [], validateInput);
  const {
    control,
    getValues,
    formState: { isDirty },
    issues,
  } = form;

  const [updateLines, { loading }] = useMutation(UPDATE_SHIPMENT_LINES_MUTATION, {
    refetchQueries: REFETCH,
  });

  const handleSave = async () => {
    if (hasCriticalIssues(issues)) {
      return;
    }
    const values = getValues();
    try {
      await updateLines({
        variables: {
          input: {
            shipmentId,
            setQuantities: [
              { lineId: line.id, quantity: Number(values.quantity), unit: values.unit },
            ],
          },
        },
      });
      alerts.success(`Updated ${line.container.containerLabel}.`);
      onClose();
    } catch (e) {
      alerts.error(e instanceof Error ? e.message : "Failed to update the line.");
    }
  };

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth="xs"
      title={`Quantity for ${line.container.containerLabel}`}
      additionalPrimaryActionElements={[<IssueListButton key="issues" issues={issues} />]}
      primaryActionButtonProps={{
        saveText: "Save",
        loading,
        disabled: hasCriticalIssues(issues) || !isDirty,
        onClick: handleSave,
      }}
    >
      <FormProvider {...form}>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Ship less than the full container (partial ship) or set the amount leaving a tank.
          </Typography>
          <FormTextField
            control={control}
            name="quantity"
            label="Quantity"
            fullWidth
            textFieldProps={{ size: "small" }}
          />
          <Controller
            control={control}
            name="unit"
            render={({ field }) => (
              <TextField
                label="Unit"
                size="small"
                select
                value={field.value}
                onChange={(e) => field.onChange(e.target.value as UnitType)}
              >
                {Object.values(UnitType).map((u) => (
                  <MenuItem key={u} value={u}>
                    {UNIT_LABELS[u] ?? u}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />
        </Stack>
      </FormProvider>
    </Dialog>
  );
}
